import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BellRing, Check, Loader2 } from "lucide-react";
import { Button } from "@core/components/ui/button";
import { Checkbox } from "@core/components/ui/checkbox";
import { Input } from "@core/components/ui/input";
import { subscribeToFinalDate } from "@core/services/FinalDateService";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface FinalDateSubscribeProps {
  pollId: string;
  defaultEmail?: string;
  className?: string;
}

/**
 * « Préviens-moi quand la date est fixée » — case NON cochée par défaut
 * (consentement explicite, garde-fou de la spec). L'e-mail est de service
 * uniquement et porte un lien de retrait.
 */
export const FinalDateSubscribe: React.FC<FinalDateSubscribeProps> = ({
  pollId,
  defaultEmail = "",
  className = "",
}) => {
  const { t, i18n } = useTranslation("polls");
  const [wanted, setWanted] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [state, setState] = useState<"idle" | "saving" | "done" | "error">("idle");

  const valid = EMAIL_RE.test(email.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setState("saving");
    try {
      await subscribeToFinalDate(pollId, email.trim(), (i18n.language || "fr").slice(0, 2));
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div
        className={`flex items-start gap-2 rounded-lg bg-success/10 p-3 text-sm text-foreground ${className}`}
        data-testid="final-date-subscribed"
      >
        <Check className="h-4 w-4 mt-0.5 text-success flex-shrink-0" aria-hidden />
        <span>{t("final_date.subscribe_done")}</span>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`rounded-lg border border-border p-3 text-left space-y-3 ${className}`}
      data-testid="final-date-subscribe"
    >
      <label className="flex items-start gap-2 text-sm text-foreground cursor-pointer">
        <Checkbox
          checked={wanted}
          onCheckedChange={(v) => setWanted(v === true)}
          className="mt-0.5"
          data-testid="final-date-subscribe-checkbox"
        />
        <span className="flex items-center gap-1.5">
          <BellRing className="h-4 w-4 text-primary" aria-hidden />
          {t("final_date.subscribe_label")}
        </span>
      </label>
      {wanted && (
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (state === "error") setState("idle");
              }}
              placeholder={t("final_date.subscribe_placeholder")}
              aria-label={t("final_date.subscribe_placeholder")}
              className="flex-1 min-w-0"
              data-testid="final-date-subscribe-email"
            />
            <Button type="submit" disabled={!valid || state === "saving"} data-testid="final-date-subscribe-submit">
              {state === "saving" && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />}
              {t("final_date.subscribe_submit")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t("final_date.subscribe_legal")}</p>
          {state === "error" && (
            <p className="text-xs text-destructive" role="alert">
              {t("final_date.subscribe_error")}
            </p>
          )}
        </div>
      )}
    </form>
  );
};

export default FinalDateSubscribe;
